import { type KeyboardEvent as ReactKeyboardEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { TaggedSelectOptionType } from './types';

/** Direction for navigating options: 1 = forward, -1 = backward */
type Direction = 1 | -1;

interface UseTaggedSelectListboxParams {
  // Options rendered in the dropdown
  options: TaggedSelectOptionType[];

  // Prevents opening and interacting with the select
  disabled: boolean;

  // Currently selected option value
  selectedValue: string;

  // Reference to the whole select used to detect clicks outside
  wrapperRef: RefObject<HTMLDivElement>;

  // Called when the user selects an option
  onSelectOption: (optionValue: string) => void;
}

interface UseTaggedSelectListboxReturn {
  // Whether the dropdown is currently open
  isOpen: boolean;

  // Index of the currently keyboard-focused option -1 means no option is focused
  focusedIndex: number;

  // Reference to the select trigger used to return focus after closing the dropdown
  triggerRef: RefObject<HTMLDivElement>;

  // Registers each option DOM element so keyboard navigation can move focus between options
  registerOptionRef: (index: number) => (element: HTMLLIElement | null) => void;

  // Closes the dropdown and resets its focus state
  closeListbox: () => void;

  // Moves focus back to the select trigger
  focusTrigger: () => void;

  // Opens or closes the dropdown
  toggleListbox: () => void;

  // Handles keyboard interaction while focus is on the select trigger
  handleTriggerKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;

  // Handles selection and keyboard navigation while focus is on an option
  handleOptionKeyDown: (event: ReactKeyboardEvent<HTMLLIElement>, index: number, optionValue: string) => void;
}

const findEnabledOptionIndex = (
  options: TaggedSelectOptionType[],
  startIndex: number,
  direction: Direction,
): number => {
  for (let index = startIndex; index >= 0 && index < options.length; index += direction) {
    if (!options[index].disabled) {
      return index;
    }
  }

  return -1;
};

const getInitialFocusedIndex = (options: TaggedSelectOptionType[], selectedValue: string): number => {
  const selectedIndex = options.findIndex((option) => option.value === selectedValue);

  if (selectedIndex >= 0 && !options[selectedIndex].disabled) {
    return selectedIndex;
  }

  return findEnabledOptionIndex(options, 0, 1);
};

export function useTaggedSelectListbox({
  options,
  disabled,
  selectedValue,
  wrapperRef,
  onSelectOption,
}: UseTaggedSelectListboxParams): UseTaggedSelectListboxReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const triggerRef = useRef<HTMLDivElement>(null);
  // Keeps references to rendered options so keyboard navigation
  // can move the actual DOM focus between them
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Keyboard opening should move focus into the list.
  // Mouse opening should keep focus on the trigger.
  const shouldFocusOptionRef = useRef(false);

  const closeListbox = useCallback((): void => {
    setIsOpen(false);
    // -1 means that no option is currently focused
    setFocusedIndex(-1);
    shouldFocusOptionRef.current = false;
  }, []);

  const focusTrigger = useCallback((): void => {
    triggerRef.current?.focus();
  }, []);

  const focusOptionAtIndex = useCallback((index: number): void => {
    if (index < 0) return;

    setFocusedIndex(index);
    optionRefs.current[index]?.focus();
  }, []);

  const openListbox = useCallback(
    (focusOption = false): void => {
      if (disabled) return;

      const initialIndex = getInitialFocusedIndex(options, selectedValue);

      // The options are rendered only after isOpen becomes true, so the focus is applied in the effect below
      shouldFocusOptionRef.current = focusOption && initialIndex >= 0;

      setFocusedIndex(initialIndex);
      setIsOpen(true);
    },
    [disabled, options, selectedValue],
  );

  const toggleListbox = useCallback((): void => {
    if (disabled) return;

    if (isOpen) {
      closeListbox();
    } else {
      openListbox();
    }
  }, [closeListbox, disabled, isOpen, openListbox]);

  const registerOptionRef = useCallback(
    (index: number) =>
      (element: HTMLLIElement | null): void => {
        optionRefs.current[index] = element;
      },
    [],
  );

  const focusAdjacentOption = useCallback(
    (currentIndex: number, direction: Direction): void => {
      // Skip disabled options while navigating with arrows
      const nextIndex = findEnabledOptionIndex(options, currentIndex + direction, direction);

      if (nextIndex >= 0) {
        focusOptionAtIndex(nextIndex);
      }
    },
    [focusOptionAtIndex, options],
  );

  const handleTriggerKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();

        if (!isOpen) {
          openListbox(true);
        } else if (focusedIndex >= 0) {
          focusOptionAtIndex(focusedIndex);
        }

        break;

      case 'Enter':
        event.preventDefault();

        if (!isOpen) {
          // Keyboard opening moves focus into the options list
          openListbox(true);
        } else if (focusedIndex >= 0) {
          focusOptionAtIndex(focusedIndex);
        }

        break;

      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          closeListbox();
        }

        break;

      default:
        break;
    }
  };

  const handleOptionKeyDown = (event: ReactKeyboardEvent<HTMLLIElement>, index: number, optionValue: string): void => {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();

        onSelectOption(optionValue);
        closeListbox();
        focusTrigger();

        break;

      case 'ArrowDown':
        event.preventDefault();
        focusAdjacentOption(index, 1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        focusAdjacentOption(index, -1);
        break;

      case 'Escape':
        event.preventDefault();

        closeListbox();
        focusTrigger();

        break;

      case 'Tab':
        // Let the browser move focus normally, just close the dropdown
        closeListbox();
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    // Close when the user clicks outside of the select
    const handleDocumentMouseDown = (event: MouseEvent): void => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        closeListbox();
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return (): void => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [closeListbox, isOpen, wrapperRef]);

  useEffect(() => {
    if (!isOpen || focusedIndex < 0) return;

    const optionElement = optionRefs.current[focusedIndex];

    // Keep the keyboard-focused option visible inside a scrollable list
    optionElement?.scrollIntoView({
      block: 'nearest',
    });

    // DOM focus can only be applied after the list has been rendered
    if (shouldFocusOptionRef.current) {
      optionElement?.focus();
      shouldFocusOptionRef.current = false;
    }
  }, [focusedIndex, isOpen]);

  return {
    isOpen,
    focusedIndex,
    triggerRef,
    registerOptionRef,
    closeListbox,
    focusTrigger,
    toggleListbox,
    handleTriggerKeyDown,
    handleOptionKeyDown,
  };
}
